import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { collection, getDocs, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import AddContentModal from "../components/AddContentModal";
import EditContentModal from "../components/EditContentModal"; // New modal for editing
import "./Content.css";

const Content = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Fetch media files from Firestore with real-time updates
  const fetchMediaFiles = useCallback(() => {
    const mediaRef = collection(db, "media");

    // Using Firestore listener for real-time updates
    const unsubscribe = onSnapshot(mediaRef, (querySnapshot) => {
      const files = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMediaFiles(files);
    });

    return () => unsubscribe(); // Clean up listener on unmount
  }, []);

  useEffect(() => {
    fetchMediaFiles();
  }, [fetchMediaFiles]);

  // Handle Edit Click
  const handleEdit = (file) => {
    setSelectedMedia(file);
    setEditModalOpen(true);
  };

  // Handle Delete Click
  const handleDelete = async (fileId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this file?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "media", fileId));
      setMediaFiles((prev) => prev.filter((file) => file.id !== fileId)); // Optimistic update
    } catch (err) {
      console.error("Error deleting file:", err.message);
    }
  };

  // Search Filter
  const filteredMediaFiles = mediaFiles.filter((file) =>
    file.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="content-container">
      {/* Header Section */}
      <div className="content-header">
        <h1>Content</h1>
        <div className="view-mode-buttons">
          <button
            className={`btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            Grid View
          </button>
          <button
            className={`btn ${viewMode === "list" ? "active" : ""}`}
            onClick={() => setViewMode("list")}
          >
            List View
          </button>
          <button className="btn add-content" onClick={() => setIsModalOpen(true)}>
            Add Content
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="content-controls">
        <input
          type="text"
          placeholder="Search media..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-bar"
        />
      </div>

      {/* Media Display */}
      {viewMode === "grid" ? (
        <div className="grid-view">
          {filteredMediaFiles.map((file) => (
            <div className="grid-item" key={file.id}>
              <div className="thumbnail">
                <img src={file.url} alt={file.name} loading="lazy" />
              </div>
              <div className="file-name">{file.name}</div>
              <div className="grid-actions">
                <button className="btn edit" onClick={() => handleEdit(file)}>Edit</button>
                <button className="btn delete" onClick={() => handleDelete(file.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Content Type</th>
                <th>Last Modified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMediaFiles.map((file) => (
                <tr key={file.id}>
                  <td>{file.name}</td>
                  <td>{file.mediaType}</td>
                  <td>{file.uploadedAt?.toDate().toLocaleDateString() || "Unknown"}</td>
                  <td>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn view"
                    >
                      View
                    </a>
                    <button className="btn edit" onClick={() => handleEdit(file)}>Edit</button>
                    <button className="btn delete" onClick={() => handleDelete(file.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Content Modal */}
      <AddContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onContentAdded={fetchMediaFiles}
      />

      {/* Edit Content Modal */}
      {selectedMedia && (
        <EditContentModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          media={selectedMedia}
          onContentUpdated={fetchMediaFiles}
        />
      )}
    </div>
  );
};

export default Content;
